import type { ExtractModuleKeys, Module } from './types';

/**
 * Default module configuration. If a customer has a different set of modules,
 * their settings are merged on top of these defaults.
 *
 * Gotchas:
 * - If the module or submodule allows tab configuration, the key / module id
 *   _must_ be the same as the Parent Type.
 * - When adding a new module, ensure that the useNavItems hook is also updated.
 * - When adding a new module, update the translation files for titles and descriptions.
 */
export const defaultModules = {
  risk: {
    enabled: true,
    allowTabConfig: true,
    subModules: {
      impact: {
        enabled: false,
      },
      risk_scoring: {
        enabled: false,
      },
      appetite: {
        enabled: true,
      },
      appetite_cascading: {
        enabled: false,
      },
      appetite_posture: {
        enabled: false,
      },
      acceptance: {
        enabled: true,
      },
      rcsa_wizard: {
        enabled: false,
      },
      risk_score_breakdown: {
        enabled: false,
      },
    },
  },
  document: {
    enabled: false,
    allowTabConfig: true,
    subModules: {
      attestation: {
        enabled: false,
      },
      public_document: {
        enabled: true,
      },
    },
  },
  obligation: {
    enabled: false,
    allowTabConfig: true,
    subModules: {
      reg_feed: {
        enabled: false,
      },
    },
  },
  third_party: {
    enabled: false,
    allowTabConfig: true,
  },
  internal_audit_entity: {
    enabled: false,
    allowTabConfig: true,
    subModules: {
      internal_audit_report: {
        enabled: true,
        allowTabConfig: true,
      },
    },
  },
  issue: {
    enabled: true,
    allowTabConfig: true,
    subModules: {
      cause: {
        enabled: true,
      },
      consequence: {
        enabled: true,
      },
      regulatory_breaches: {
        enabled: true,
      },
    },
  },
  control: {
    enabled: true,
    allowTabConfig: true,
    subModules: {
      control_group: {
        enabled: true,
      },
      control_test_smart_copy: {
        enabled: false,
      },
      control_test_submitter_autopopulate: {
        enabled: false,
      },
    },
  },
  action: {
    enabled: true,
    allowTabConfig: true,
  },
  asset: {
    enabled: false,
    allowTabConfig: true,
  },
  indicator: {
    enabled: true,
    allowTabConfig: false,
  },
  assessment: {
    enabled: true,
    allowTabConfig: true,
    subModules: {
      oversight_register_and_findings: {
        enabled: false,
        allowTabConfig: true,
      },
    },
  },
  incident_reporting: {
    enabled: true,
  },
  approval: {
    enabled: false,
  },
  custom_datasource: {
    enabled: false,
  },
  scheduler: {
    enabled: false,
  },
  notification: {
    enabled: true,
  },
  enterprise_risk: {
    enabled: false,
    allowTabConfig: true,
  },
  ai: {
    enabled: true,
    subModules: {
      chat: {
        enabled: false,
      },
      chat_warning: {
        enabled: false,
      },
      suggested_controls: {
        enabled: false,
      },
      writing_assistant: {
        enabled: true,
      },
      // Read-only in-app Agent mode (M1, RSP-6223). Beta-org gated; never GA
      // until the RSP-6227 security sign-off.
      agent: {
        enabled: false,
      },
    },
  },
  integrations: {
    enabled: false,
    subModules: {
      zapier_self_managed: {
        enabled: true,
      },
      zapier_by_risksmart: {
        enabled: true,
      },
      mcp_server_integrations: {
        enabled: true,
      },
      mcp_personal: {
        enabled: true,
      },
      rest_api: {
        enabled: true,
      },
      slack: {
        enabled: false,
      },
    },
  },
  external_api: {
    enabled: false,
  },
  settings_webhooks: {
    enabled: false,
  },
  auth: {
    enabled: true,
    subModules: {
      scim_configuration: {
        enabled: false,
      },
      sso_configuration: {
        enabled: false,
      },
      public_access: {
        enabled: true,
      },
    },
  },
  alt_values: {
    enabled: false,
  },
  no_inherit: {
    enabled: false,
  },
} as const satisfies Record<string, Module>;

/**
 * Union of all valid module key paths derived from `defaultModules`.
 * Use this as the parameter type for `useIsModuleEnabled`, `isModuleEnabled`, etc.
 * to get compile-time validation of module keys.
 *
 * Examples: 'risk' | 'risk.subModules.impact' | 'document.subModules.attestation' | ...
 */
export type ModuleKey = ExtractModuleKeys<typeof defaultModules>;
